import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type Quill from "quill";
import { IClickedMention } from "./use-quill";
import { MENTION_TYPE } from "modules/utils/Constants";
import { highlightSearchTerms } from "./quill-highlight-search-terms";

interface IProps {
  delta?: string | null;
  searchTerms?: string[];
}

const useQuillViewer = ({ delta, searchTerms }: IProps) => {
  const quillRef = useRef<Quill | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [clickedMention, setClickedMention] = useState<IClickedMention | null>(
    null,
  );
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);

  useEffect(() => {
    if (!container) return;

    let cancelled = false;

    const init = async () => {
      const { default: Quill } = await import("quill");
      await import("quill-mention/autoregister");
      const { createCustomMentionBlot } = await import(
        "./mentions/mention-blot"
      );

      Quill.register(createCustomMentionBlot(Quill), true);

      if (cancelled) return;

      const editorContainer = container.appendChild(
        container.ownerDocument.createElement("div"),
      );

      const quill = new Quill(editorContainer, {
        theme: "snow",
        readOnly: true,
        modules: {
          toolbar: false,
        },
      });

      const onClick = (e: MouseEvent) => {
        const mention = (e.target as HTMLElement).closest(
          ".mention",
        ) as HTMLElement | null;
        if (mention) {
          const rect = mention.getBoundingClientRect();
          setClickedMention({
            id: mention.dataset.id ?? "",
            type: (mention.dataset.type as MENTION_TYPE) ?? "",
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
          return;
        }
        setClickedMention(null);
      };

      // Quill's clipboard module preventDefaults every copy/cut event, then
      // silently writes nothing when the selection extends past this editor's
      // root (e.g. a selection spanning into the timestamp or another
      // message), leaving the clipboard unchanged. Stop the event in the
      // capture phase so Quill's bubble-phase listener never runs and the
      // browser's native copy applies.
      const onCopyCapture = (e: ClipboardEvent) => {
        e.stopPropagation();
      };

      quill.root.addEventListener("click", onClick);
      quill.root.addEventListener("copy", onCopyCapture, true);
      quill.root.addEventListener("cut", onCopyCapture, true);
      quillRef.current = quill;
      setIsReady(true);

      cleanupRef.current = () => {
        quill.root.removeEventListener("click", onClick);
        quill.root.removeEventListener("copy", onCopyCapture, true);
        quill.root.removeEventListener("cut", onCopyCapture, true);
      };
    };

    init();

    return () => {
      cancelled = true;
      cleanupRef.current?.();
      quillRef.current = null;
      setIsReady(false);
      container.innerHTML = "";
    };
  }, [container]);

  // layout effect: set contents before the paint in which isReady flips, so
  // consumers can swap a placeholder for the editor without an empty frame
  useLayoutEffect(() => {
    if (!isReady || !quillRef.current || !delta) return;
    try {
      const parsed = JSON.parse(delta);
      quillRef.current.setContents(parsed, "silent");
    } catch {
      quillRef.current.clipboard.dangerouslyPasteHTML(delta);
    }

    if (searchTerms && searchTerms.length > 0) {
      highlightSearchTerms(quillRef.current, searchTerms);
    }
  }, [isReady, delta, searchTerms]);

  const closeClickedMention = useCallback(() => {
    setClickedMention(null);
  }, []);

  return { containerRef, clickedMention, closeClickedMention, isReady };
};

export default useQuillViewer;
